import type {
  CollectiblePlacement,
  Gap,
  LevelData,
  ObstaclePlacement,
  OpenEdge,
  Vec3Lit,
} from './types';

// "Bamboo Descent" — the v1 course (§5/§11). Authored entirely as data and
// consumed by the generic runner in World/TrackBuilder. A winding, net-downhill
// kart-style track that runs along a valley floor: grassy bamboo hills rise on
// both sides and keep you on the track, so falling is rare and deliberate —
// just a couple of open cliffs and one chasm to jump. The pace rises toward the
// finish via a steepening grade (§2).

const DESC = 33; // descent control points (0..DESC); the finish is at DESC
const PLATEAU = 4; // flat run-out points beyond the finish
const controlPoints: Vec3Lit[] = [];
const halfWidths: number[] = [];
const banks: number[] = [];

{
  let y = 96;
  let z = 0;
  for (let i = 0; i <= DESC; i++) {
    const p = i / DESC;
    // Two combined sines: broad S-curves plus tighter switchbacks late.
    const x = 24 * Math.sin(p * Math.PI * 3.1) + 11 * Math.sin(p * Math.PI * 6.5 + 0.8);
    controlPoints.push({ x, y, z });

    // Widen through the branch section, pinch the late switchback.
    let hw = 6.5;
    if (p > 0.56 && p < 0.68) hw = 11; // branch: room for two lanes
    else if (p > 0.72 && p < 0.82) hw = 6; // late switchback (not too narrow)
    else if (p > 0.86) hw = 8; // open finish straight
    halfWidths.push(hw);

    // Advance downhill; grade steepens through the middle, then tapers to flat
    // over the last stretch so the finish sits on level ground (§2).
    const taper = p < 0.85 ? 1 : 1 - (p - 0.85) / 0.15;
    const grade = (0.11 + p * 0.34) * taper;
    const dz = 30;
    z -= dz;
    y -= dz * grade;
  }

  // Flat run-out plateau: continue the final heading at constant height so the
  // panda rolls out onto level ground past the finish instead of off a ledge.
  const last = controlPoints[DESC];
  const prev = controlPoints[DESC - 1];
  const dx = last.x - prev.x;
  const dz = last.z - prev.z;
  const len = Math.hypot(dx, dz) || 1;
  const ux = dx / len;
  const uz = dz / len;
  let px = last.x;
  let pz = last.z;
  for (let k = 1; k <= PLATEAU; k++) {
    px += ux * 28;
    pz += uz * 28;
    controlPoints.push({ x: px, y, z: pz }); // y held flat
    halfWidths.push(10); // wide, open finish plateau
  }

  // Banking: tilt into the turns based on discrete heading change.
  const M = controlPoints.length - 1;
  for (let i = 0; i <= M; i++) {
    const a = controlPoints[Math.max(0, i - 1)];
    const b = controlPoints[i];
    const c = controlPoints[Math.min(M, i + 1)];
    const inDir = { x: b.x - a.x, z: b.z - a.z };
    const outDir = { x: c.x - b.x, z: c.z - b.z };
    const cross = inDir.x * outDir.z - inDir.z * outDir.x;
    const mag = Math.hypot(inDir.x, inDir.z) * Math.hypot(outDir.x, outDir.z) || 1;
    const turn = cross / mag;
    banks.push(Math.max(-0.32, Math.min(0.32, -turn * 0.9)));
  }
}

// The single chasm to jump — placed on a straight section so the bounce-jump
// lands cleanly on track (a jump on a curve drifts you off the side). Short,
// with a full row of bounce pads. Falling in respawns you on the far side (§5).
const GAP: Gap = { tStart: 0.4, tEnd: 0.406, exitT: 0.42 };

// Sparse open cliffs — the only spots where the side hills don't catch you.
const openEdges: OpenEdge[] = [
  { side: 'right', tStart: 0.2, tEnd: 0.27 }, // tempting wide outside line
  { side: 'left', tStart: 0.74, tEnd: 0.8 }, // on the tight switchback
];

// Densely scatter fruit to draw lines; weave the lateral offset (§4). Skip any
// that would float over the chasm.
const fruit: CollectiblePlacement[] = [];
for (let i = 0; i < 46; i++) {
  const t = 0.04 + (i / 46) * 0.92;
  if (t >= GAP.tStart - 0.02 && t <= GAP.tEnd + 0.02) continue;
  const lateral = 0.55 * Math.sin(t * Math.PI * 9 + 0.5);
  fruit.push({ t, lateral, tier: 'fruit' });
}

const collectibles: CollectiblePlacement[] = [
  ...fruit,
  // Chinese food on riskier outer lines (incl. near the cliffs).
  { t: 0.23, lateral: 0.92, tier: 'food' }, // out toward the right cliff
  { t: 0.25, lateral: 0.85, tier: 'food' },
  { t: 0.32, lateral: -0.8, tier: 'food' },
  { t: 0.5, lateral: -0.6, tier: 'food' }, // after the jump landing
  { t: 0.62, lateral: -0.85, tier: 'food' }, // collectible side of the branch
  { t: 0.65, lateral: -0.8, tier: 'food' },
  { t: 0.77, lateral: 0.55, tier: 'food' }, // inside line of the switchback
  { t: 0.88, lateral: -0.7, tier: 'food' },
  // Coins: sparse, on ramps & risky lines (kept low so they're easy to grab).
  { t: 0.31, lateral: 0.0, tier: 'coin', height: 2.2 }, // over the first ramp
  { t: 0.6, lateral: 0.85, tier: 'coin' }, // outer lane of the branch
  { t: 0.64, lateral: 0.85, tier: 'coin' },
  // Finish run-in: a few fruit + a dumpling over the flat last stretch so it
  // leads into the finish-line coin instead of coasting across empty ground.
  { t: 0.95, lateral: -0.3, tier: 'fruit' },
  { t: 0.965, lateral: 0.0, tier: 'food' },
  { t: 0.98, lateral: 0.25, tier: 'fruit' },
  // Red envelope jackpots — worth going out of your way for, but reachable.
  // (The big ramp at 0.83 carries just the envelope — no coin stacked on it.)
  { t: 0.24, lateral: 0.85, tier: 'envelope' }, // near the cliff edge
  { t: 0.83, lateral: 0.0, tier: 'envelope', height: 3.0 }, // over the big ramp
];

const obstacles: ObstaclePlacement[] = [
  { t: 0.13, lateral: 0.2, type: 'rock' },
  { t: 0.28, lateral: -0.3, type: 'log' },
  { t: 0.52, lateral: 0.0, type: 'fence' },
  { t: 0.68, lateral: -0.4, type: 'log' },
  { t: 0.7, lateral: 0.4, type: 'rock' },
  { t: 0.86, lateral: -0.2, type: 'fence' },
  { t: 0.91, lateral: 0.3, type: 'log' },
];

export const LEVEL_1: LevelData = {
  id: 'bamboo-descent',
  name: 'Bamboo Descent',
  // Generous par: you get a time bonus for any relaxed finish, and going faster
  // just earns more (§4). A clean run is well under this.
  parTime: 135,
  controlPoints,
  halfWidths,
  banks,
  bankHeight: 9,
  finishControlIndex: DESC,
  openEdges,
  gaps: [GAP],
  // Three surfaces only: grass (default), ice (fast/slippery), mud (slow). Mud
  // sits off to one side as a little blob you weave around — never across the
  // centre line, so the coin/fruit line is always avoidable. Ice (the fun, fast
  // surface) can span the whole width.
  surfaceZones: [
    { tStart: 0.27, tEnd: 0.31, surface: 'mud', lateralMin: 0.25, lateralMax: 1 }, // right-side blob
    { tStart: 0.66, tEnd: 0.72, surface: 'ice' }, // risky fast corner
    { tStart: 0.74, tEnd: 0.82, surface: 'ice' }, // slippery switchback
    { tStart: 0.87, tEnd: 0.9, surface: 'mud', lateralMin: -1, lateralMax: -0.25 },
  ],
  ramps: [
    { t: 0.31, lateral: 0.0, length: 0.03, rise: 2.6, width: 0.5 },
    { t: 0.82, lateral: 0.0, length: 0.045, rise: 4.2, width: 0.6 },
  ],
  bouncePads: [
    // A full-width row of springs right before the chasm — whatever line you're
    // on (even hugging a wall), you get a big friendly boing across.
    { t: 0.395, lateral: -1.0 },
    { t: 0.395, lateral: -0.5 },
    { t: 0.395, lateral: 0.0 },
    { t: 0.395, lateral: 0.5 },
    { t: 0.395, lateral: 1.0 },
    { t: 0.7, lateral: -0.4 },
    { t: 0.88, lateral: 0.3 },
  ],
  collectibles,
  obstacles,
  checkpointCount: 8,
};
